import React, { useState, useMemo } from 'react';
import './ImportPreviewModal.css';

/**
 * Import Preview Modal
 * Shows validated students from backend with:
 * - Color-coded rows (green=valid, yellow=warning, red=error, blue=new)
 * - Summary statistics boxes (updated in real-time)
 * - Issues display for each student
 * - Inline editing with real-time validation
 * - "Import Valid Only" button for partial imports
 */
const ImportPreviewModal = ({
  validationResult,
  onConfirm,
  onCancel,
  tahuns,
  kelasList,
  sizeConfig
}) => {
  const [students, setStudents] = useState(validationResult?.students || []);
  const [activeFilter, setActiveFilter] = useState(null); // null = show all, 'valid', 'warnings', 'errors', 'new', 'update'

  // Real-time validation function for a single student
  const validateStudent = (student) => {
    const issues = [];
    const validUniformSizes = sizeConfig?.uniformSizes || [];
    const validShoeSizes = sizeConfig?.shoeSizes || [];

    // 1. Check if gender is set
    if (!student.jantina) {
      issues.push({ type: 'error', icon: '❌', message: 'Gender is required' });
      return issues; // Cannot validate further without gender
    }

    const gender = student.jantina.toUpperCase();

    // Helper function to check if a field is actually filled (not empty, not "-", not whitespace)
    const isFilled = (value) => value && value !== '-' && value.trim() !== '';

    // 2. MALE VALIDATION (L = Lelaki)
    if (gender === 'L' || gender === 'LELAKI') {
      // Baju Putih is mandatory
      if (!isFilled(student.bajuPutih)) {
        issues.push({ type: 'error', icon: '❌', message: 'Baju Putih is required for male students' });
      } else if (validUniformSizes.length > 0 && !validUniformSizes.includes(student.bajuPutih)) {
        issues.push({ type: 'error', icon: '❌', message: `Invalid Baju Putih size: ${student.bajuPutih}` });
      }

      // EXACTLY ONE of Seluar Panjang OR Seluar Pendek
      if (!isFilled(student.seluarPanjang) && !isFilled(student.seluarPendek)) {
        issues.push({ type: 'error', icon: '❌', message: 'Exactly one of Seluar Panjang or Seluar Pendek is required' });
      } else if (isFilled(student.seluarPanjang) && isFilled(student.seluarPendek)) {
        issues.push({ type: 'error', icon: '❌', message: 'Only one of Seluar Panjang or Seluar Pendek can be selected' });
      } else if (isFilled(student.seluarPanjang) && validUniformSizes.length > 0 && !validUniformSizes.includes(student.seluarPanjang)) {
        issues.push({ type: 'error', icon: '❌', message: `Invalid Seluar Panjang size: ${student.seluarPanjang}` });
      } else if (isFilled(student.seluarPendek) && validUniformSizes.length > 0 && !validUniformSizes.includes(student.seluarPendek)) {
        issues.push({ type: 'error', icon: '❌', message: `Invalid Seluar Pendek size: ${student.seluarPendek}` });
      }

      // Kasut is mandatory
      if (!isFilled(student.kasut)) {
        issues.push({ type: 'error', icon: '❌', message: 'Kasut is required' });
      } else if (validShoeSizes.length > 0 && !validShoeSizes.includes(student.kasut)) {
        issues.push({ type: 'error', icon: '❌', message: `Invalid Kasut size: ${student.kasut}` });
      }

      // Check for gender violations (male cannot have female items)
      if (isFilled(student.bajuKurung)) {
        issues.push({ type: 'error', icon: '❌', message: 'Male students cannot have Baju Kurung' });
      }
      if (isFilled(student.kainKurung)) {
        issues.push({ type: 'error', icon: '❌', message: 'Male students cannot have Kain Kurung' });
      }
      if (isFilled(student.pinafore)) {
        issues.push({ type: 'error', icon: '❌', message: 'Male students cannot have Pinafore' });
      }
    }

    // 3. FEMALE VALIDATION (P = Perempuan)
    else if (gender === 'P' || gender === 'PEREMPUAN') {
      // EXACTLY ONE SET: (BajuPutih + Pinafore) OR (BajuKurung + KainKurung)
      const hasBajuPinaforeSet = isFilled(student.bajuPutih) && isFilled(student.pinafore) && !isFilled(student.bajuKurung) && !isFilled(student.kainKurung);
      const hasKurungSet = isFilled(student.bajuKurung) && isFilled(student.kainKurung) && !isFilled(student.bajuPutih) && !isFilled(student.pinafore);

      if (!hasBajuPinaforeSet && !hasKurungSet) {
        issues.push({ type: 'error', icon: '❌', message: 'Exactly one set required: (Baju Putih + Pinafore) OR (Baju Kurung + Kain Kurung)' });
      } else if (hasBajuPinaforeSet) {
        // Validate Baju Putih size
        if (validUniformSizes.length > 0 && !validUniformSizes.includes(student.bajuPutih)) {
          issues.push({ type: 'error', icon: '❌', message: `Invalid Baju Putih size: ${student.bajuPutih}` });
        }
        // Validate Pinafore size
        if (validUniformSizes.length > 0 && !validUniformSizes.includes(student.pinafore)) {
          issues.push({ type: 'error', icon: '❌', message: `Invalid Pinafore size: ${student.pinafore}` });
        }
      } else if (hasKurungSet) {
        // Validate Baju Kurung size
        if (validUniformSizes.length > 0 && !validUniformSizes.includes(student.bajuKurung)) {
          issues.push({ type: 'error', icon: '❌', message: `Invalid Baju Kurung size: ${student.bajuKurung}` });
        }
        // Validate Kain Kurung size
        if (validUniformSizes.length > 0 && !validUniformSizes.includes(student.kainKurung)) {
          issues.push({ type: 'error', icon: '❌', message: `Invalid Kain Kurung size: ${student.kainKurung}` });
        }
      }

      // Kasut is mandatory
      if (!isFilled(student.kasut)) {
        issues.push({ type: 'error', icon: '❌', message: 'Kasut is required' });
      } else if (validShoeSizes.length > 0 && !validShoeSizes.includes(student.kasut)) {
        issues.push({ type: 'error', icon: '❌', message: `Invalid Kasut size: ${student.kasut}` });
      }

      // Check for gender violations (female cannot have male items)
      if (isFilled(student.seluarPanjang)) {
        issues.push({ type: 'error', icon: '❌', message: 'Female students cannot have Seluar Panjang' });
      }
      if (isFilled(student.seluarPendek)) {
        issues.push({ type: 'error', icon: '❌', message: 'Female students cannot have Seluar Pendek' });
      }
    }

    return issues;
  };

  // Handle inline field edit with real-time validation
  const handleFieldEdit = (studentIndex, field, value) => {
    const updatedStudents = [...students];
    updatedStudents[studentIndex][field] = value;

    // Re-validate the student after edit
    const validationIssues = validateStudent(updatedStudents[studentIndex]);
    updatedStudents[studentIndex].issues = validationIssues;

    console.log(`ImportPreviewModal: Field '${field}' updated for student ${studentIndex + 1}, validation result:`, validationIssues.length === 0 ? 'VALID' : `${validationIssues.length} issue(s)`);

    setStudents(updatedStudents);
  };

  // Determine row CSS class based on validation status
  const getRowClass = (student) => {
    if (student.issues?.some(i => i.type === 'error')) return 'row-error';
    if (student.issues?.some(i => i.type === 'warning')) return 'row-warning';
    if (student.isNew) return 'row-new';
    return 'row-valid';
  };

  // Calculate summary statistics dynamically (updates in real-time)
  const summary = useMemo(() => {
    const validCount = students.filter(s => !s.issues || s.issues.length === 0).length;
    const warningCount = students.filter(s => s.issues?.some(i => i.type === 'warning') && !s.issues.some(i => i.type === 'error')).length;
    const errorCount = students.filter(s => s.issues?.some(i => i.type === 'error')).length;
    const newCount = students.filter(s => s.importAction === 'ADD' || s.isNew).length;
    const updateCount = students.filter(s => s.importAction === 'UPDATE').length;

    return {
      valid: validCount,
      warnings: warningCount,
      errors: errorCount,
      newStudents: newCount,
      updateStudents: updateCount
    };
  }, [students]);

  // Check if there are errors
  const hasErrors = summary.errors > 0;

  // Get valid students only (for "Import Valid Only" button)
  const validStudents = students.filter(s => !s.issues || !s.issues.some(i => i.type === 'error'));

  // Filter students based on active filter
  const filteredStudents = useMemo(() => {
    if (!activeFilter) return students; // Show all

    switch (activeFilter) {
      case 'valid':
        return students.filter(s => !s.issues || s.issues.length === 0);
      case 'warnings':
        return students.filter(s => s.issues?.some(i => i.type === 'warning') && !s.issues.some(i => i.type === 'error'));
      case 'errors':
        return students.filter(s => s.issues?.some(i => i.type === 'error'));
      case 'new':
        return students.filter(s => s.importAction === 'ADD' || s.isNew);
      case 'update':
        return students.filter(s => s.importAction === 'UPDATE');
      default:
        return students;
    }
  }, [students, activeFilter]);

  // Handle filter toggle
  const handleFilterToggle = (filterType) => {
    if (activeFilter === filterType) {
      setActiveFilter(null); // Reset filter (show all)
      console.log(`ImportPreviewModal: Filter reset, showing all ${students.length} students`);
    } else {
      setActiveFilter(filterType);
      console.log(`ImportPreviewModal: Filter set to '${filterType}'`);
    }
  };

  // Handle "Import Valid Only" button click
  const handleImportValidOnly = () => {
    if (validStudents.length === 0) {
      return; // Should not happen, button should be disabled
    }

    console.log(`ImportPreviewModal: Importing ${validStudents.length} valid students only`);
    onConfirm(validStudents);
  };

  // Handle "Import All" button click (with confirmation if errors exist)
  const handleImportAll = () => {
    if (hasErrors) {
      // Show confirmation dialog when importing with errors
      const errorCount = summary.errors;
      const confirmMessage =
        `⚠️ ${errorCount} record(s) have errors.\n\n` +
        `Import anyway? You can fix them later in the main grid.\n\n` +
        `Click OK to import all ${students.length} records, or Cancel to go back.`;

      if (window.confirm(confirmMessage)) {
        console.log(`ImportPreviewModal: User confirmed importing ${students.length} students (including ${errorCount} with errors)`);
        onConfirm(students);
      } else {
        console.log('ImportPreviewModal: User cancelled import with errors');
      }
    } else {
      // No errors, import directly
      console.log(`ImportPreviewModal: Importing all ${students.length} students`);
      onConfirm(students);
    }
  };

  return (
    <>
      <div className="import-overlay" onClick={onCancel}></div>
      <div className="preview-modal">
        {/* Header */}
        <div className="preview-modal-header">
          <h3>Preview Import Results</h3>
          <button className="close-button" onClick={onCancel}>&times;</button>
        </div>

        {/* Summary Statistics (Clickable Filter Buttons) */}
        <div className="summary-boxes">
          <button
            className={`summary-box box-valid ${activeFilter === 'valid' ? 'active' : ''}`}
            onClick={() => handleFilterToggle('valid')}
            title="Click to filter valid records only"
          >
            VALID: {summary.valid || 0}
          </button>
          <button
            className={`summary-box box-warnings ${activeFilter === 'warnings' ? 'active' : ''}`}
            onClick={() => handleFilterToggle('warnings')}
            title="Click to filter warnings only"
          >
            WARNINGS: {summary.warnings || 0}
          </button>
          <button
            className={`summary-box box-errors ${activeFilter === 'errors' ? 'active' : ''}`}
            onClick={() => handleFilterToggle('errors')}
            title="Click to filter errors only"
          >
            ERRORS: {summary.errors || 0}
          </button>
          <button
            className={`summary-box box-new ${activeFilter === 'new' ? 'active' : ''}`}
            onClick={() => handleFilterToggle('new')}
            title="Click to filter new students only"
          >
            NEW: {summary.newStudents || 0}
          </button>
          <button
            className={`summary-box box-update ${activeFilter === 'update' ? 'active' : ''}`}
            onClick={() => handleFilterToggle('update')}
            title="Click to filter updates only"
          >
            UPDATES: {summary.updateStudents || 0}
          </button>
        </div>

        {/* Preview Table */}
        <div className="preview-table-container">
          <table className="preview-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Action</th>
                <th>Name</th>
                <th>NoKP</th>
                <th>Gender</th>
                <th>Tahun</th>
                <th>Kelas</th>
                <th>Baju Putih</th>
                <th>Seluar Panjang</th>
                <th>Seluar Pendek</th>
                <th>Baju Kurung</th>
                <th>Kain Kurung</th>
                <th>Pinafore</th>
                <th>Kasut</th>
                <th>Issues</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="16" style={{ textAlign: 'center', padding: '40px', color: '#999', fontSize: '14px' }}>
                    <div>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔍</div>
                      <strong>No records match this filter</strong>
                      <div style={{ marginTop: '8px' }}>Click the active filter button again to show all records</div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredStudents.map((student, index) => (
                  <tr key={index} className={getRowClass(student)}>
                    <td>{student.sourceRow || index + 1}</td>
                  <td>
                    <span className={`action-badge action-${student.importAction?.toLowerCase()}`}>
                      {student.importAction || 'ADD'}
                    </span>
                  </td>
                  <td className="cell-name">{student.name}</td>
                  <td className="cell-ic">{student.noKP}</td>
                  <td className="cell-gender">
                    {student.jantina?.toUpperCase().charAt(0) || student.jantina}
                  </td>

                  {/* Editable: Tahun */}
                  <td className="cell-tahun">
                    <select
                      value={student.tahun || ''}
                      onChange={(e) => handleFieldEdit(index, 'tahun', e.target.value)}
                      className="inline-edit"
                    >
                      <option value="">-</option>
                      {tahuns?.map(t => (
                        <option key={t.tahun} value={t.tahun}>{t.tahun}</option>
                      ))}
                    </select>
                  </td>

                  {/* Editable: Kelas */}
                  <td className="cell-kelas">
                    <select
                      value={student.class || ''}
                      onChange={(e) => handleFieldEdit(index, 'class', e.target.value)}
                      className="inline-edit"
                    >
                      <option value="">-</option>
                      {kelasList?.map(kelas => (
                        <option key={kelas} value={kelas}>{kelas}</option>
                      ))}
                    </select>
                  </td>

                  {/* Editable: Baju Putih */}
                  <td>
                    <select
                      value={student.bajuPutih || ''}
                      onChange={(e) => handleFieldEdit(index, 'bajuPutih', e.target.value)}
                      className="inline-edit"
                    >
                      <option value="">-</option>
                      {sizeConfig?.uniformSizes?.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </td>

                  {/* Editable: Seluar Panjang */}
                  <td>
                    <select
                      value={student.seluarPanjang || ''}
                      onChange={(e) => handleFieldEdit(index, 'seluarPanjang', e.target.value)}
                      className="inline-edit"
                    >
                      <option value="">-</option>
                      {sizeConfig?.uniformSizes?.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </td>

                  {/* Editable: Seluar Pendek */}
                  <td>
                    <select
                      value={student.seluarPendek || ''}
                      onChange={(e) => handleFieldEdit(index, 'seluarPendek', e.target.value)}
                      className="inline-edit"
                    >
                      <option value="">-</option>
                      {sizeConfig?.uniformSizes?.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </td>

                  {/* Editable: Baju Kurung */}
                  <td>
                    <select
                      value={student.bajuKurung || ''}
                      onChange={(e) => handleFieldEdit(index, 'bajuKurung', e.target.value)}
                      className="inline-edit"
                    >
                      <option value="">-</option>
                      {sizeConfig?.uniformSizes?.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </td>

                  {/* Editable: Kain Kurung */}
                  <td>
                    <select
                      value={student.kainKurung || ''}
                      onChange={(e) => handleFieldEdit(index, 'kainKurung', e.target.value)}
                      className="inline-edit"
                    >
                      <option value="">-</option>
                      {sizeConfig?.uniformSizes?.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </td>

                  {/* Editable: Pinafore */}
                  <td>
                    <select
                      value={student.pinafore || ''}
                      onChange={(e) => handleFieldEdit(index, 'pinafore', e.target.value)}
                      className="inline-edit"
                    >
                      <option value="">-</option>
                      {sizeConfig?.uniformSizes?.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </td>

                  {/* Editable: Kasut */}
                  <td className="cell-kasut">
                    <select
                      value={student.kasut || ''}
                      onChange={(e) => handleFieldEdit(index, 'kasut', e.target.value)}
                      className="inline-edit"
                    >
                      <option value="">-</option>
                      {sizeConfig?.shoeSizes?.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </td>

                  {/* Issues */}
                  <td className="cell-issues">
                    {student.issues?.length > 0 ? (
                      <div className="issues-list">
                        {student.issues.map((issue, i) => (
                          <div key={i} className={`issue issue-${issue.type}`}>
                            {issue.icon} {issue.message}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="no-issues">✓ OK</span>
                    )}
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="preview-modal-footer">
          <div className="footer-info">
            {activeFilter ? (
              <span style={{ color: '#1976d2', fontWeight: '600' }}>
                📋 Showing {filteredStudents.length} of {students.length} record(s)
                {filteredStudents.length === 0 && ' - No records match this filter'}
              </span>
            ) : hasErrors ? (
              <span className="footer-error">
                {summary.errors} error(s), {validStudents.length} valid record(s)
              </span>
            ) : (
              <span className="footer-success">
                All {students.length} record(s) are valid
              </span>
            )}
          </div>
          <div className="footer-buttons">
            <button className="btn-cancel" onClick={onCancel}>
              Cancel
            </button>

            {/* Import Valid Only Button - Show when there are errors and valid students */}
            {hasErrors && validStudents.length > 0 && (
              <button
                className="btn-import-valid"
                onClick={handleImportValidOnly}
                style={{
                  background: '#ff9800',
                  color: 'white',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
                onMouseOver={(e) => e.target.style.background = '#f57c00'}
                onMouseOut={(e) => e.target.style.background = '#ff9800'}
              >
                Import Valid Only ({validStudents.length})
              </button>
            )}

            {/* Import All Button - Always enabled, shows confirmation if errors exist */}
            <button
              className="btn-confirm"
              onClick={handleImportAll}
              title={hasErrors ? `Import all ${students.length} records (including ${summary.errors} with errors)` : `Import all ${students.length} records`}
            >
              Import All ({students.length})
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImportPreviewModal;
